import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex, ScrollVFlex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useTagStore from '@/stores/TagStore';

import SettingHeader from '../Header';
import CreateTagModal from './CreateTagModal';
import TagSettingItem from './TagSettingItem';

const TagSettingBody = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { tagMap } = useTagStore((state) => ({
    tagMap: state.tagMap,
  }));
  const [hideList, setHideList] = useState<string[]>([]);

  const lv1TagNodes = useMemo(() => {
    return Object.values(tagMap).filter((tag) => tag.level === 1);
  }, [tagMap]);

  const onClickExpandAll = () => {
    setHideList([]);
  };

  const onClickCollapseAll = () => {
    setHideList(lv1TagNodes.map((tag) => tag.alias));
  };

  const createTagModalState = useModalState();

  return (
    <Style height="100%">
      <SettingHeader
        title="Tags"
        rightNode={
          <Flex g={8}>
            <Button sizeType="m" variant="secondary" onClick={onClickExpandAll}>
              Expand
            </Button>
            <Button
              sizeType="m"
              variant="secondary"
              onClick={onClickCollapseAll}
            >
              Collapse
            </Button>
            <Button
              sizeType="m"
              variant="primary"
              onClick={() => createTagModalState.open()}
            >
              Create tag
            </Button>
          </Flex>
        }
      />
      <Modal
        status={createTagModalState.status}
        closeModal={createTagModalState.close}
      >
        <CreateTagModal
          onCreateTagSuccess={() => {
            scrollRef.current?.scrollTo({
              top: scrollRef.current.scrollHeight,
              behavior: 'smooth',
            });
          }}
        />
      </Modal>

      <ScrollVFlex f={1} padding="32px" ref={scrollRef}>
        <VFlex g={8} style={{ maxWidth: 800 }}>
          {lv1TagNodes.map((lv1TagNode) => {
            // List up level-1 tags
            return (
              <React.Fragment key={lv1TagNode.alias}>
                <TagItemWrapper d="column">
                  <TagSettingItem
                    tag={lv1TagNode}
                    hideList={hideList}
                    setHideList={setHideList}
                  />
                  <VFlex>
                    {lv1TagNode.children.map((lv2TagNodeAlias) => {
                      // List up level-2 tags
                      return (
                        <React.Fragment key={lv2TagNodeAlias}>
                          <TagSettingItem
                            tag={tagMap[lv2TagNodeAlias]}
                            hideList={hideList}
                            setHideList={setHideList}
                          />
                          <VFlex>
                            {tagMap[lv2TagNodeAlias]?.children.map(
                              (lv3TagNodeAlias) => {
                                // List up level-3 tags
                                return (
                                  <TagSettingItem
                                    tag={tagMap[lv3TagNodeAlias]}
                                    key={lv3TagNodeAlias}
                                    hideList={hideList}
                                    setHideList={setHideList}
                                  />
                                );
                              },
                            )}
                          </VFlex>
                        </React.Fragment>
                      );
                    })}
                  </VFlex>
                </TagItemWrapper>
              </React.Fragment>
            );
          })}
        </VFlex>
      </ScrollVFlex>
    </Style>
  );
};

const Style = styled(VFlex)``;

const TagItemWrapper = styled(Flex)`
  border: 1px solid ${(props) => props.theme.colors.gray300};
  border-radius: 8px;
`;

export default TagSettingBody;
